import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Upload, FileText, CheckCircle2, XCircle, Clock, AlertTriangle, RotateCcw,
  Receipt, Loader2, Plus, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const STATUS_COLOR = {
  collecting: "bg-zinc-100 text-zinc-700",
  extracting: "bg-blue-100 text-blue-700",
  imported: "bg-emerald-100 text-emerald-700",
  rolled_back: "bg-rose-100 text-rose-700",
};

export default function HistoricalImports() {
  const nav = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [sourceCrm, setSourceCrm] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/historical-imports/batches");
      setBatches(data.batches || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load batches");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!label.trim()) { toast.error("Enter a batch name"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/historical-imports/batches", {
        label: label.trim(), source_crm: sourceCrm.trim() || null,
      });
      setNewOpen(false); setLabel(""); setSourceCrm("");
      toast.success("Batch created — upload files next");
      nav(`/app/imports/historical/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="hist-imports-page">
      <button onClick={() => nav("/app/imports")} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4"/> Data Import
      </button>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Data migration</div>
          <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
            <Receipt className="w-8 h-8 text-[#FF5A00]"/> Historical invoices
          </h1>
          <p className="text-zinc-500 mt-1 max-w-2xl">
            Upload old invoices from your previous CRM. LeadForge extracts each one with AI,
            matches customers, and recreates them as native invoices — historical revenue lands
            on the correct month.
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="hist-new-batch-btn">
          <Plus className="w-4 h-4 mr-1"/> New import batch
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-100 bg-zinc-50 text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
          <div className="col-span-4">Batch</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-1 text-right">Files</div>
          <div className="col-span-2 text-right">Revenue imported</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Created</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-zinc-500">Loading…</div>
        ) : batches.length === 0 ? (
          <div className="p-10 text-center text-zinc-500" data-testid="hist-imports-empty">
            No import batches yet. Click <strong>New import batch</strong> to start.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {batches.map((b) => (
              <Link key={b.id} to={`/app/imports/historical/${b.id}`} data-testid={`hist-batch-row-${b.id}`}
                className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-zinc-50">
                <div className="col-span-4">
                  <div className="font-medium">{b.label}</div>
                  <div className="text-xs text-zinc-500">by {b.created_by} · batch <span className="font-mono-accent">{b.id.slice(-6)}</span></div>
                </div>
                <div className="col-span-2 text-sm text-zinc-700">{b.source_crm || "—"}</div>
                <div className="col-span-1 text-right text-sm font-mono-accent">
                  {Object.values(b.counts || {}).reduce((a, x) => a + x, 0)}
                </div>
                <div className="col-span-2 text-right text-sm font-mono-accent">
                  <div>{money(b.totals?.gross)}</div>
                  {b.totals?.count ? <div className="text-[10px] text-zinc-500">{b.totals.count} invoices</div> : null}
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center text-[10px] uppercase font-mono-accent px-2 py-0.5 rounded ${STATUS_COLOR[b.status] || "bg-zinc-100 text-zinc-700"}`}>
                    {b.status}
                  </span>
                </div>
                <div className="col-span-1 text-right text-[11px] text-zinc-500">
                  {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="hist-new-batch-dialog">
          <DialogHeader>
            <DialogTitle>Create import batch</DialogTitle>
            <DialogDescription>
              A batch groups files from a single migration attempt. You can review, correct, and
              approve everything before any invoice is created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Batch name</Label>
              <Input value={label} onChange={(e)=>setLabel(e.target.value)}
                     placeholder="Jobber migration — 2023-2025" data-testid="hist-new-batch-label"/>
            </div>
            <div>
              <Label>Source CRM (optional)</Label>
              <Input value={sourceCrm} onChange={(e)=>setSourceCrm(e.target.value)}
                     placeholder="Jobber, Housecall Pro, QuickBooks, etc."
                     data-testid="hist-new-batch-source"/>
            </div>
            <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-100 rounded p-2">
              🛡 Nothing is billed to customers. Emails, SMS, and automations are suppressed for
              every invoice created from this batch.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setNewOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={creating}
                    className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                    data-testid="hist-new-batch-save">
              {creating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Creating…</> : "Create batch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
